<template>
  <van-popup
    v-model:show="visible"
    position="center"
    round
    class="card-code-popup"
    :close-on-click-overlay="false"
    @open="resetCardCode"
  >
    <section class="card-code-dialog">
      <h2>{{ t("mine.cardCodeRedemption") }}</h2>
      <input
        v-model="cardCodeInput"
        class="card-code-dialog__input"
        type="text"
        autocomplete="off"
        :placeholder="t('mine.enterCardCode')"
      />
      <div class="card-code-dialog__actions">
        <button
          type="button"
          class="card-code-dialog__button card-code-dialog__button--cancel"
          :disabled="submittingCardCode"
          @click="visible = false"
        >
          {{ t("common.cancel") }}
        </button>
        <button
          type="button"
          class="card-code-dialog__button card-code-dialog__button--confirm"
          :disabled="submittingCardCode"
          @click="submitCardCode"
        >
          {{ t("common.confirm") }}
        </button>
      </div>
    </section>
  </van-popup>
</template>

<script setup>
import { computed, ref } from "vue";
import { closeToast, showFailToast, showLoadingToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { courseRedeem } from "@/api/card";
import { showPassedToast } from "@/utils/toast";
import { useProfileStore } from "@/stores/profile";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:show"]);
const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();
const cardCodeInput = ref("");
const submittingCardCode = ref(false);
const visible = computed({
  get: () => props.show,
  set: (value) => emit("update:show", value),
});

function resetCardCode() {
  cardCodeInput.value = "";
}

async function submitCardCode() {
  if (submittingCardCode.value) {
    return;
  }

  const cardNum = String(cardCodeInput.value || "").trim();
  if (!cardNum) {
    showFailToast(t("mine.enterCardCode"));
    return;
  }
  if (!profileStore.platformAddress) {
    visible.value = false;
    router.replace("/login");
    return;
  }

  submittingCardCode.value = true;
  showLoadingToast({
    message: t("common.submitting"),
    forbidClick: true,
    duration: 0,
  });
  try {
    await courseRedeem({ code: cardNum });
    closeToast();
    visible.value = false;
    cardCodeInput.value = "";
    showPassedToast(t("wallet.activateSuccess"));
  } catch (error) {
    closeToast();
    showFailToast(error?.message || error?.msg || t("common.retryLater"));
  } finally {
    submittingCardCode.value = false;
  }
}
</script>

<style scoped lang="scss">
:global(.card-code-popup.van-popup--center) {
  width: 335px;
  max-width: calc(100vw - 40px);
  height: 193px;
  overflow: hidden;
  border-radius: 20px;
  background: #ffffff;
}

.card-code-dialog {
  height: 193px;
  padding: 20px;
  box-sizing: border-box;
}

.card-code-dialog h2 {
  margin: 0;
  color: #000000;
  font-size: 18px;
  font-weight: 600;
  line-height: 22px;
  text-align: center;
}

.card-code-dialog__input {
  display: block;
  width: 100%;
  height: 44px;
  margin: 24px 0 0;
  padding: 0 16px;
  border: 0;
  border-radius: 10px;
  outline: none;
  box-sizing: border-box;
  background: #f5f5f5;
  color: #222222;
  font-size: 14px;
  line-height: 18px;
}

.card-code-dialog__input::placeholder {
  color: #999999;
  opacity: 1;
}

.card-code-dialog__actions {
  display: flex;
  gap: 8px;
  width: 100%;
  margin-top: 24px;
}

.card-code-dialog__button {
  flex: 1;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 100px;
  font-size: 16px;
  font-weight: 500;
  line-height: 40px;
  text-align: center;
}

.card-code-dialog__button--cancel {
  background: #f0f0f0;
  color: #666666;
}

.card-code-dialog__button--confirm {
  background: var(--wp-color-brand-1, #7442e8);
  color: #ffffff;
}
</style>
